import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { withStyles } from '@material-ui/core/styles';
import { doFetch, fetchClear } from '@trdk/style/Redux/fetch.actions';
import {
  Grid, Paper, Typography, Divider, Button, FormControlLabel,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
} from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import TitleField from '../components/title-field';
import StandardInput from '../components/standardinput';
import { violationsSkjenk, violationsSalg, violationsTobacco } from '../components/violations';
import {
  getElementBySysId, formatDate, formatDateTime, getControllersListCommaSep,
  getViolationSeverityStr, getLicenseHeader, CONTROL_STATUS_CREATED, CONTROL_STATUS_STARTED,
  CONTROL_STATUS_NEEDS_ADDITIONAL_REPORT, CONTROL_STATUS_SUBMITTED, CONTROL_STATUS_CANCELLED,
  CONTROL_STATUS_CLOSED, goBack, dateTimeToString, convertDateStrToUseSlash,
} from '../utils';
import { fetchAssignments, fetchControl } from '../services/fetch/load-form-service';
import NotificationBox from '../components/notification-box';
import { createOrUpdateControl } from '../services/submit/submit-servce';

function getFormHeader(controlPayload) {
  if (controlPayload.status === CONTROL_STATUS_STARTED) {
    return 'Ny kontroll';
  } if (controlPayload.status === CONTROL_STATUS_NEEDS_ADDITIONAL_REPORT) {
    return `Tilleggsrapport etter kontroll ${formatDate(controlPayload.startTime)}`;
  }
  return `Kontrollrapport ${formatDate(controlPayload.startTime)}`;
}

function calculateViolationsSeverity(allViolations, selectedViolations) {
  let severity = 0;
  if (allViolations != null && selectedViolations != null) {
    allViolations.forEach((violation) => {
      if (severity < 2
        && selectedViolations[violation.name] != null
        && selectedViolations[violation.name]) {
        severity = violation.requiresAdditionalReport ? 2 : 1;
      }
    });
  }
  return severity;
}

const validationSchema = (
  violationsSeverityManual, violationsSeverity, calculatedViolationsSeverity,
) => Yup.object().shape({
  stopTime: Yup.date().required('Feltet er påkrevd').nullable(),
  responsibleName: Yup.string().required('Feltet er påkrevd').nullable(),
  remarkVisible: (
    violationsSeverityManual && violationsSeverity > 0)
    || (!violationsSeverityManual && calculatedViolationsSeverity > 0)
    ? Yup.string().required('Feltet er påkrevd').nullable() : Yup.string().nullable(),
});

const validationSchemaAdditionalReport = Yup.object().shape({
  additionalReport: Yup.string().required('Feltet er påkrevd').nullable(),
});

class Control extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      submitted: false,
      submitError: false,
    };
  }

  componentDidMount() {
    this.fetchControl();
  }

  fetchControl = () => {
    const {
      dispatch,
      match: { params: { controlID } },
    } = this.props;
    dispatch(doFetch('CONTROL', fetchControl(controlID)
      .then((res) => {
        if (!res.ok) { throw res; }
        return res.json();
      })));
  }

  render() {
    const {
      classes, dispatch, history, match: { params: { controlID, licenseID } },
      licenses: { payload: licensesPayload },
      control: {
        fetching, fetched, error, payload: controlPayload,
      },
    } = this.props;
    const {
      submitting, submitted, submitError,
    } = this.state;

    if ((fetching && !fetched) || (submitting && !submitted)) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    if (fetched && error) {
      return (<Typography>En feil har oppstått under henting av valgt kontroll.</Typography>);
    }

    const license = getElementBySysId(licensesPayload, licenseID);
    if (license == null) {
      return (
        <div>
          {`Ingen bevilling funnet med sysId: ${licenseID}`}
        </div>
      );
    }

    const isTobaccoLicense = license.licenseType === 'tobakk' || license.licenseType === 'tobakk-midlertidig';

    let allViolations = null;
    if (license.licenseType === 'skjenk' || license.licenseType === 'ea-skjenk') {
      allViolations = violationsSkjenk;
    } else if (license.licenseType === 'salg' || license.licenseType === 'ea-salg') {
      allViolations = violationsSalg;
    } else {
      allViolations = violationsTobacco;
    }

    const initialViolations = controlPayload.violations ? controlPayload.violations : {};

    const violationsSeverity = controlPayload.violationsSeverity
      ? controlPayload.violationsSeverity : 0;

    const violationsSeverityManual = (
      violationsSeverity !== calculateViolationsSeverity(allViolations, initialViolations)
    );

    const isStatusCreated = controlPayload.status === CONTROL_STATUS_CREATED;
    const isStatusStarted = controlPayload.status === CONTROL_STATUS_STARTED;
    const isStatusAdditionalReport = (
      controlPayload.status === CONTROL_STATUS_NEEDS_ADDITIONAL_REPORT
    );
    const isStatusSubmitted = (
      controlPayload.status === CONTROL_STATUS_SUBMITTED
      || controlPayload.status === CONTROL_STATUS_CANCELLED
      || controlPayload.status === CONTROL_STATUS_CLOSED
    );

    const isAdditionalReportSubmitted = controlPayload.additionalReport && controlPayload.additionalReport !== '';

    // We never expect to get a status=created in this form.
    // When we create a new control in new-control form, the status should be set to 'started'
    if (isStatusCreated
      || (!isStatusStarted && !isStatusAdditionalReport && !isStatusSubmitted)
    ) {
      if (controlPayload.status === undefined) {
        return ('');
      }
      return (
        <Typography variant="subtitle1">
          {`Uventet status på kontroll-oppføring i systemet: '${controlPayload.status}'`}
        </Typography>
      );
    }

    return (
      <div>
        <TitleField
          title={getFormHeader(controlPayload)}
          subtitle={getLicenseHeader(license, true)}
        />
        <Formik
          validationSchema={
            isStatusAdditionalReport ? validationSchemaAdditionalReport
              : validationSchema(
                violationsSeverityManual, violationsSeverity, calculateViolationsSeverity,
              )
          }
          initialValues={{
            controllnr: '',
            violations: initialViolations,
            violationsSeverity,
            violationsSeverityManual,
            endNow: true,
            stopTime: new Date(),
            remarkVisible: controlPayload.remarkVisible,
            remarkCaseWorker: controlPayload.remarkCaseWorker,
            responsibleName: controlPayload.responsibleName,
            additionalReport: controlPayload.additionalReport ? controlPayload.additionalReport : '',
          }}
          onSubmit={(v) => {
            this.setState({ submitted: false });
            this.setState({ submitting: true });

            const violationsSeveritySubmit = v.violationsSeverityManual
              ? v.violationsSeverity
              : calculateViolationsSeverity(allViolations, v.violations);

            let submitPayload = {};

            if (controlPayload.status === CONTROL_STATUS_STARTED) {
              submitPayload = {
                stopTime: v.endNow ? new Date() : v.stopTime,
                status: violationsSeveritySubmit === 2
                  ? CONTROL_STATUS_NEEDS_ADDITIONAL_REPORT : CONTROL_STATUS_SUBMITTED,
                violations: v.violations,
                violationsSeverity: violationsSeveritySubmit,
                remarkVisible: v.remarkVisible !== '' ? v.remarkVisible : null,
                remarkCaseWorker: v.remarkCaseWorker !== '' ? v.remarkCaseWorker : null,
                responsibleName: v.responsibleName,
              };
              submitPayload.stopTime = dateTimeToString(submitPayload.stopTime, false);
            } else if (controlPayload.status === CONTROL_STATUS_NEEDS_ADDITIONAL_REPORT) {
              submitPayload = {
                additionalReport: v.additionalReport,
                additionalReportDate: new Date(),
              };
            }
            submitPayload.sysId = controlID;

            createOrUpdateControl(submitPayload, 'PUT')
              .then((res) => {
                this.setState({ submitting: false });
                this.setState({ submitted: true });
                if (!res.ok) {
                  this.setState({ submitError: true });
                  throw res;
                }
                dispatch(fetchClear('CONTROL'));

                if (isAdditionalReportSubmitted || isStatusAdditionalReport
                  || submitPayload.status === CONTROL_STATUS_NEEDS_ADDITIONAL_REPORT) {
                  window.location.reload();
                } else {
                  dispatch(doFetch('ASSIGNMENTS', fetchAssignments().then((assignmentsRes) => {
                    if (!assignmentsRes.ok) { throw assignmentsRes; }
                    return assignmentsRes.json();
                  })));

                  goBack(history);
                }
              });
          }}
        >
          {({
            values, handleChange, errors, touched, handleBlur, initialValues, setFieldValue,
          }) => {
            const combo = {
              classes, errors, touched, handleBlur, initialValues,
            };

            const startDateTime = new Date(convertDateStrToUseSlash(controlPayload.startTime));

            const calculatedViolationsSeverity = calculateViolationsSeverity(
              allViolations, values.violations,
            );

            return (
              <Form>
                <Paper className={classes.paper} elevation={2}>
                  <Grid container spacing={2}>
                    {isStatusAdditionalReport && !isAdditionalReportSubmitted && (
                      <Grid item xs={12}>
                        <NotificationBox
                          variant="info"
                          header="Tilleggsrapport trengs"
                          text={`Siden det er avdekket ${isTobaccoLicense ? '' : 'grove '}brudd/avvik, må det sendes inn en tilleggsrapport. Denne må sendes inn senest 2 dager etter at kontrollen ble gjennomført.`}
                          showIcon
                          margin="2rem 0"
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography color="primary">Informasjon</Typography>
                    </Grid>
                    <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                      <Typography variant="body1">
                        <strong>Styrer: </strong>
                        {license.people.licenseManager ? `${license.people.licenseManager.firstName} ${license.people.licenseManager.lastName}` : '(ingen styrer registrert)'}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Stedfortreder: </strong>
                        {license.people.licenseDeputy ? `${license.people.licenseDeputy.firstName} ${license.people.licenseDeputy.lastName}` : '(ingen stedfortreder registrert)'}
                      </Typography>
                    </Grid>

                    <StandardInput
                      disabled
                      label="Kontrollører"
                      size={6}
                      value={getControllersListCommaSep(controlPayload.controllers)}
                      onChange={handleChange}
                      combo={combo}
                    />
                    <StandardInput
                      disabled
                      label="Start-tid"
                      value={formatDateTime(controlPayload.startTime)}
                      size={6}
                      onChange={handleChange}
                      combo={combo}
                    />
                    {(!isStatusStarted) && (
                      <StandardInput
                        disabled
                        label="Slutt-tid"
                        value={formatDateTime(controlPayload.stopTime)}
                        size={6}
                        onChange={handleChange}
                        combo={combo}
                      />
                    )}
                    {isStatusStarted && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            className={classes.formControl}
                            control={(
                              <Checkbox
                                checked={values.endNow}
                                value={values.endNow}
                                name="endNow"
                                color="primary"
                              />
                            )}
                            onChange={(e) => {
                              setFieldValue('stopTime', new Date());
                              setFieldValue('endNow', e.target.checked);
                            }}
                            onBlur={handleBlur}
                            label="Sett slutt-tid til klokkeslettet når kontrollen sendes inn"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          {!values.endNow && (
                            <FormControl className={classes.formControl}>
                              <KeyboardDateTimePicker
                                ampm={false}
                                name="stopTime"
                                value={values.stopTime}
                                error={!!(errors.stopTime && touched.stopTime)}
                                label="Slutt-tid"
                                disabled={values.endNow}
                                required={!values.endNow && isStatusStarted}
                                minDate={startDateTime}
                                maxDate={new Date().setDate(startDateTime.getDate() + 1)}
                                showTodayButton
                                todayLabel="I dag"
                                cancelLabel="Avbryt"
                                minDateMessage="Kan ikke være før start-tid"
                                maxDateMessage="Kan ikke være mer enn ett døgn etter start-tid"
                                format="dd.MM.yyyy HH:mm"
                                invalidDateMessage="Ugyldig dato"
                                onChange={(e) => {
                                  setFieldValue('stopTime', e);
                                }}
                                onBlur={(e) => {
                                  handleBlur(e);
                                }}
                              />
                            </FormControl>
                          )}
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography color="primary">Rapportering av brudd</Typography>
                    </Grid>
                    {allViolations.map(item => (
                      <Grid
                        item
                        xs={12}
                        xl={6}
                        key={JSON.stringify(item)}
                        className={classes.violationGrid}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              disabled={!isStatusStarted}
                              name={`violations.${[item.name]}`}
                              checked={values.violations[item.name]}
                              color="primary"
                            />
                          )}
                          onChange={(e) => {
                            setFieldValue(`violations.${[item.name]}`, e.target.checked);
                            handleChange(e);
                          }}
                          label={item.label}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography color="primary">Oppsummering</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl className={classes.formControl}>
                        <InputLabel>Type brudd/avvik</InputLabel>
                        <Select
                          name="violationSeverity"
                          value={
                            values.violationsSeverityManual
                              ? values.violationsSeverity : calculatedViolationsSeverity}
                          disabled={
                            isStatusAdditionalReport
                            || isStatusSubmitted
                            || !values.violationsSeverityManual
                          }
                          onChange={(e) => {
                            if (values.violationsSeverityManual) {
                              setFieldValue('violationsSeverity', e.target.value);
                            }
                            handleChange(e);
                          }}
                        >
                          <MenuItem value={0}>{getViolationSeverityStr(0)}</MenuItem>
                          <MenuItem value={1}>{getViolationSeverityStr(1)}</MenuItem>
                          <MenuItem value={2}>{getViolationSeverityStr(2)}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {isStatusStarted && (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              name="violationsSeverityManual"
                              checked={values.violationsSeverityManual}
                              color="primary"
                            />
                          )}
                          onChange={(e) => {
                            setFieldValue('violationsSeverity', calculatedViolationsSeverity);
                            setFieldValue('violationsSeverityManual', e.target.checked);
                            handleChange(e);
                          }}
                          label="Sett type brudd/avvik manuelt"
                        />
                      </Grid>
                    )}
                    <StandardInput
                      name="remarkVisible"
                      label="Anmerkning (synlig for bevillingshaver)"
                      disabled={!isStatusStarted}
                      inputProps={{
                        autoComplete: 'off',
                        maxLength: 5000,
                      }}
                      required={
                        (values.violationsSeverityManual && values.violationsSeverity > 0)
                        || (!values.violationsSeverityManual && calculatedViolationsSeverity > 0)
                      }
                      size={12}
                      onChange={handleChange}
                      combo={combo}
                      multiline
                      rows={4}
                    />
                    <StandardInput
                      name="remarkCaseWorker"
                      label="Anmerkning til saksbehandler"
                      disabled={!isStatusStarted}
                      inputProps={{
                        autoComplete: 'off',
                        maxLength: 5000,
                      }}
                      size={12}
                      onChange={handleChange}
                      combo={combo}
                      multiline
                      rows={4}
                    />
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <StandardInput
                      name="responsibleName"
                      label="Navn ansvarshavende"
                      required={isStatusStarted}
                      disabled={!isStatusStarted}
                      inputProps={{
                        autoComplete: 'off',
                        maxLength: 200,
                      }}
                      size={12}
                      onChange={handleChange}
                      combo={combo}
                    />
                    {(isStatusAdditionalReport
                      || (isStatusSubmitted && controlPayload.violationsSeverity > 0)
                    ) && (
                      <>
                        {isAdditionalReportSubmitted && (
                          <StandardInput
                            name="additionalReportDate"
                            label="Dato tilleggsrapport"
                            disabled
                            size={12}
                            value={formatDate(controlPayload.additionalReportDate)}
                            onChange={handleChange}
                            combo={combo}
                          />
                        )}
                        <StandardInput
                          name="additionalReport"
                          label="Tilleggsrapport"
                          size={12}
                          inputProps={{
                            autoComplete: 'off',
                            maxLength: 5000,
                          }}
                          disabled={isStatusSubmitted}
                          onChange={handleChange}
                          combo={combo}
                          multiline
                          rows={6}
                        />
                        {isAdditionalReportSubmitted && !isStatusSubmitted && (
                          <Grid item xs={12}>
                            <NotificationBox
                              variant="info"
                              header="Tilleggsrapport sendt"
                              text="Tilleggsrapporten over har blitt sendt inn til saksbehandler. Det vil være mulig å redigere tilleggsrapporten til og med 2 dager etter kontroll-datoen."
                              showIcon
                              margin="2rem 0"
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {isStatusStarted && (
                      <>
                        {((!values.violationsSeverityManual && calculatedViolationsSeverity === 2)
                          || (values.violationsSeverityManual && values.violationsSeverity === 2))
                          && (
                            <>
                              <Grid item xs={12}><Divider /></Grid>
                              <Grid item xs={12}>
                                <Typography>
                                  <strong>
                                    {`Ved ${isTobaccoLicense ? '' : 'grove '}brudd/avvik må det sendes inn en tilleggsrapport i etterkant.`}
                                  </strong>

                                </Typography>
                              </Grid>
                            </>
                          )}
                      </>
                    )}
                    {!isStatusSubmitted && (
                      <>
                        <Grid item xs={12}><Divider /></Grid>
                        <Grid item xs={12}>
                          <Button color="primary" type="submit" variant="contained" fullWidth>
                            Send
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Form>
            );
          }}
        </Formik>
        {submitted && submitError && (
          <NotificationBox variant="error" header="Feil oppsto under lagring av endringer" showIcon margin="2rem 0" />
        )}
      </div>
    );
  }
}

const styles = theme => ({
  loading: {
    margin: theme.spacing(15),
    textAlign: 'center',
  },
  paper: {
    padding: '0.8rem',
  },
  radio: {
    flexDirection: 'initial',
    justifyContent: 'space-evenly',
  },
  violationGrid: {
    paddingTop: '0px !important',
    // paddingBottom: '0px !important',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '10rem',
    maxWidth: '100%',
  },
  errorLabel: {
    color: '#e41f1a',
    fontSize: '0.75rem',
    margin: theme.spacing(1),
  },
});

Control.propTypes = {
  match: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  licenses: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
};

const mapStateToProps = ({ licenses, control }) => ({ licenses, control });

export default connect(mapStateToProps)(withRouter(withStyles(styles)(Control)));
